// TermsAndConditions.jsx
import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const TermsAndConditions = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full min-h-screen bg-gray-100 p-8">
      <div className="max-w-5xl mx-auto bg-white p-8 rounded-md shadow-sm">

        <h1 className="text-3xl font-bold mb-6">
          Terms and Conditions
        </h1>

        <p className="mb-4">
          <strong>Last Updated:</strong> March 10, 2026
        </p>

        <p className="mb-4">
          These Terms and Conditions ("Terms") govern your access to and
          use of the Ashlar Building Group website and services. By
          using our website or services, you agree to these Terms. If
          you do not agree, do not use our website or services.
        </p>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          1. Definitions
        </h2>

        <p className="mb-2">
          <strong>Company:</strong> refers to Ashlar Building Group LLC
          ("we", "us", or "our"), a California-licensed general
          contractor (License #1130737).
        </p>

        <p className="mb-2">
          <strong>Client / You:</strong> any individual or entity using
          our website or services.
        </p>

        <p className="mb-2">
          <strong>Services:</strong> construction and remodeling
          services including full home remodels, kitchen remodels,
          bathroom remodels, ADUs, JADUs, garage conversions, ground-up
          custom homes, HOA & commercial projects, takeover projects,
          and related offerings.
        </p>

        <p className="mb-2">
          <strong>Website:</strong> refers to
          https://www.ashlarbuildinggroup.com and related pages.
        </p>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          2. Eligibility and Authority
        </h2>

        <p className="mb-2">
          By using our Services, you confirm that you are at least 18
          years old, a legal resident or authorized entity in the United
          States, and have full authority to enter into these Terms on
          behalf of yourself or any organization you represent.
        </p>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          3. Scope of Services
        </h2>

        <p className="mb-2">
          We provide professional construction and remodeling services
          including but not limited to full home remodels, kitchen
          remodels, bathroom remodels, home additions, ADUs, JADUs,
          garage conversions, ground-up custom homes, HOA & commercial
          building rehabilitation, and specialty takeover projects.
          Specific project scope, timelines, deliverables, and costs are
          defined in written agreements, contracts, or proposals
          provided separately.
        </p>

        <p className="mb-2">
          Nothing on this website constitutes a binding contract or
          guarantee of service availability. All projects are subject to
          a signed agreement and payment terms.
        </p>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          4. Contact Form & Inquiries
        </h2>

        <p className="mb-2">
          Submitting a contact form or inquiry through our website does
          not constitute a binding contract or guarantee of service. It
          initiates a consultation process only. We will follow up to
          discuss your project and determine if we are the right fit.
        </p>

        <p className="mb-2">
          By submitting a form, you consent to being contacted via email
          or phone by Ashlar Building Group LLC regarding your inquiry.
        </p>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          5. Use of Website
        </h2>

        <p className="mb-2">
          You agree not to use this Website for any unlawful purpose or
          in a way that violates these Terms. The following is a
          non-exhaustive list of prohibited uses:
        </p>

        <ul className="list-disc list-inside text-gray-700 space-y-2 mb-4 ml-2">
          <li>
            Impersonating or attempting to impersonate Ashlar Building
            Group LLC or its employees, representatives, subsidiaries,
            or divisions;
          </li>

          <li>
            Misrepresenting your identity or affiliation with any person
            or entity;
          </li>

          <li>
            Sending or attempting to send any advertising or promotional
            material, including spam, junk mail, chain mail, or similar
            material;
          </li>

          <li>
            Engaging in any conduct that restricts or inhibits any
            person's use or enjoyment of the Website;
          </li>

          <li>
            Using the Website in any manner that could disable,
            overburden, damage, or impair the Website;
          </li>

          <li>
            Using any robot, spider, or other automated technology to
            access or monitor the Website without authorization;
          </li>

          <li>
            Using any device, software, or routine that interferes with
            the proper working of the Website, including viruses, trojan
            horses, worms, or logic bombs;
          </li>

          <li>
            Attempting to gain unauthorized access to, interfere with,
            damage, or disrupt any parts of the Website or its servers;
          </li>

          <li>
            Attempting to attack the Website via denial-of-service or
            distributed denial-of-service attacks;
          </li>

          <li>
            Using the Website in any way that violates applicable
            federal, state, or local laws, rules, or regulations.
          </li>
        </ul>

        <p className="mb-2">
          Violations may result in restricted access and potential legal
          action.
        </p>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          6. Client Responsibilities
        </h2>

        <p className="mb-2">
          You agree to provide accurate and complete information,
          cooperate in a timely manner to facilitate project delivery,
          use all deliverables and services lawfully, obtain any
          required approvals or permits on your end, and comply with all
          applicable local, state, and federal laws.
        </p>

        {/* Section 7 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          7. Fees, Payments, and Invoicing
        </h2>

        <p className="mb-2">
          Fees and payment schedules are defined in individual project
          agreements or contracts. Payments are due as invoiced.
          Deposits may be required to begin work and may be
          non-refundable depending on project phase. Late or missed
          payments may result in work stoppage or termination of
          services.
        </p>

        {/* Section 8 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          8. Licensing & Compliance
        </h2>

        <p className="mb-2">
          Ashlar Building Group LLC holds California Contractor's
          License #1130737. All work is performed in compliance with
          applicable California Building Codes, Title 24 energy
          requirements, and local municipal regulations. Permits are
          pulled as required by law.
        </p>

        {/* Section 9 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          9. Intellectual Property
        </h2>

        <p className="mb-2">
          All website content, branding, photography, design assets, and
          written materials are owned by Ashlar Building Group LLC and
          protected by applicable intellectual property laws. You may
          not reproduce, distribute, or use any content without prior
          written permission.
        </p>

        <p className="mb-2">
          We retain ownership of pre-existing tools, methods, templates,
          and processes. Project-specific deliverables may be licensed
          or transferred as agreed in writing.
        </p>

        <p className="mb-2">
          If you believe in good faith that any content on this Website
          infringes your intellectual property rights, please contact us
          at{" "}
          <a
            href="mailto:info@ashlarbuildinggroup.com"
            className="text-blue-600"
          >
            info@ashlarbuildinggroup.com
          </a>{" "}
          with a written notice titled{" "}
          <em>
            "Infringement of Intellectual Property Rights – DMCA,"
          </em>{" "}
          including your signature, a description of the infringed work,
          your contact information, and a good faith statement that the
          use is unauthorized.
        </p>

        {/* Section 10 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          10. Confidentiality
        </h2>

        <p className="mb-2">
          Both parties agree to maintain confidentiality of sensitive
          business, financial, or project-related information unless
          required by law or permitted in writing by the disclosing
          party.
        </p>

       {/* Section 11 */}
<h2 className="text-2xl font-semibold mt-8 mb-4">
  11. SMS Terms & Conditions
</h2>

<div className="space-y-6 text-gray-700">

  {/* NEW SMS BLOCK */}
  <div className="bg-gray-50 border border-gray-200 p-5 rounded-md">
    <p className="mb-3 font-medium">
      By providing your phone number, you consent to receive SMS messages from
      Ashlar Building Group regarding appointments, project updates, reminders,
      and customer support.
    </p>

    <ul className="list-disc list-inside ml-4 space-y-1 text-gray-700">
      <li>Message frequency may vary.</li>
      <li>Message and data rates may apply.</li>
      <li>Reply <strong>STOP</strong> to opt out.</li>
      <li>Reply <strong>HELP</strong> for assistance.</li>
      <li>Carriers are not liable for delayed or undelivered messages.</li>
      <li>
        For more information, please review our{" "}
        <Link to="/privacy-policy" className="text-blue-600 underline">
          Privacy Policy
        </Link>
        .
      </li>
    </ul>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.1 SMS Consent Communication
    </h3>

    <p>
      The information (including phone numbers) obtained as part of the SMS consent
      process will not be shared with third parties for marketing purposes.
    </p>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.2 Types of SMS Communications
    </h3>

    <p className="mb-2">
      If you have consented to receive text messages from Ashlar Building Group LLC,
      you may receive SMS communications related to:
    </p>

    <ul className="list-disc list-inside ml-4 space-y-1">
      <li>Appointment confirmations</li>
      <li>Appointment reminders</li>
      <li>Project updates</li>
      <li>Customer support responses</li>
      <li>Service-related notifications</li>
    </ul>

    <p className="mt-3 text-sm italic text-gray-600">
      Example: "Hello, this is a reminder about your upcoming project consultation
      with Ashlar Building Group LLC. Reply STOP to opt out of SMS messaging at any time."
    </p>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.3 Message Frequency
    </h3>

    <p>Message frequency varies.</p>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.4 Potential Fees for SMS Messaging
    </h3>

    <p>
      Message and data rates may apply depending on your mobile carrier plan.
    </p>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.5 Opt-In Method
    </h3>

    <p className="mb-2">
      You may opt in to receive SMS messages by:
    </p>

    <ul className="list-disc list-inside ml-4 space-y-1">
      <li>Submitting a website contact form</li>
      <li>Providing consent during consultations or service requests</li>
    </ul>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.6 Opt-Out Method
    </h3>

    <p>
      You may opt out of SMS communications at any time by replying{" "}
      <strong>STOP</strong> to any SMS message.
    </p>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.7 Help & Support
    </h3>

    <p className="mb-2">
      For assistance, reply <strong>HELP</strong> to any SMS message or contact us directly at:
    </p>

    <p>
      Email:{" "}
      <a href="mailto:info@ashlarbuildinggroup.com" className="text-blue-600">
        info@ashlarbuildinggroup.com
      </a>
    </p>

    <p>
      Phone:{" "}
      <a href="tel:+19256557741" className="text-blue-600">
        (925) 655-7741
      </a>
    </p>
  </div>

  <div>
    <h3 className="font-semibold mb-2">
      11.8 Standard Messaging Disclosures
    </h3>

    <ul className="list-disc list-inside ml-4 space-y-1">
      <li>Message and data rates may apply</li>
      <li>Message frequency varies</li>
      <li>You can opt out anytime by replying STOP</li>
      <li>Reply HELP for assistance</li>
    </ul>
  </div>

  <div>
    <p>
      No mobile information will be shared with third parties/affiliates for marketing/promotional purposes.
      Information sharing to subcontractors in support services, such as customer service, is permitted.
      All other use case categories exclude text messaging originator opt-in data and consent; this information
      will not be shared with any third parties.
    </p>
  </div>

</div>

        {/* Section 12 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          12. Third-Party Services
        </h2>

        <p className="mb-2">
          We may use third-party vendors, subcontractors, or platforms
          to deliver services or operate our website. We are not
          responsible for the performance, systems, outages, or policies
          of third parties outside our direct control. Subcontractors
          used on projects are vetted and supervised by Ashlar Building
          Group LLC.
        </p>

        <p className="mb-2">
          This Website may contain hyperlinks to third-party websites.
          We are not responsible for their contents or privacy
          practices. Our inclusion of such links does not imply any
          endorsement of the material on those websites.
        </p>

        {/* Section 13 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          13. Disclaimer of Warranties
        </h2>

        <p className="mb-2">
          Our website and its content are provided "as is" and "as
          available." We disclaim all warranties of any kind, whether
          express or implied, including fitness for a particular purpose
          or non-infringement. We do not guarantee that information on
          the website is error-free or up to date.
        </p>

        <p className="mb-2">
          We assume no liability for the availability, errors, or
          inaccuracies of the information, products, or services
          provided on this Website. Products or services may be
          incorrectly described or unavailable. We reserve the right to
          correct any errors on our Website at any time.
        </p>

        {/* Section 14 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          14. Limitation of Liability
        </h2>

        <p className="mb-2">
          To the fullest extent permitted by law, Ashlar Building Group
          LLC shall not be liable for any direct, indirect, punitive,
          incidental, special, or consequential damages arising out of
          or in any way connected with your access to, display of, or
          use of this Website or Services — including but not limited to
          reliance on opinions or information appearing on this Website,
          computer viruses, linked third-party websites, or products and
          services obtained through this Website.
        </p>

        <p className="mb-2">
          Our total liability to you for any claim shall not exceed the
          fees paid by you for the specific services in question.
        </p>

        {/* Section 15 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          15. Indemnification
        </h2>

        <p className="mb-2">
          You agree to indemnify, defend, and hold harmless Ashlar
          Building Group LLC and its officers, employees, and agents
          from any claims, liabilities, damages, or expenses arising
          from your misuse of services, violation of these Terms, or
          infringement of any third-party rights.
        </p>

        {/* Section 16 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          16. Termination
        </h2>

        <p className="mb-2">
          We reserve the right to suspend or terminate services or
          website access if these Terms are violated, payments are
          overdue, the engagement poses a legal or operational risk, or
          at our sole discretion with reasonable notice. Termination
          does not relieve payment obligations already incurred.
        </p>

        {/* Section 17 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          17. Governing Law and Jurisdiction
        </h2>

        <p className="mb-2">
          These Terms are governed by and construed in accordance with
          the laws of the State of California, without regard to
          conflict-of-law principles. Any disputes arising under these
          Terms shall be resolved exclusively in state or federal courts
          located in Alameda County, California.
        </p>

        <p className="mb-2">
          You and Ashlar Building Group LLC agree that each may bring
          claims against the other only in your or its individual
          capacity, and not as a plaintiff or class member in any class
          or representative action.
        </p>

        {/* Section 18 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          18. Changes to Terms
        </h2>

        <p className="mb-2">
          We may update these Terms periodically. Changes will be posted
          on this page with an updated "Last Updated" date. Continued
          use of the website or services after changes constitutes your
          acceptance of the revised Terms.
        </p>

        {/* Section 19 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">
          19. Contact Information
        </h2>

        <p className="mb-2">
          Email:{" "}
          <a
            href="mailto:info@ashlarbuildinggroup.com"
            className="text-blue-600"
          >
            info@ashlarbuildinggroup.com
          </a>
        </p>

        <p className="mb-2">
          Phone:{" "}
          <a
            href="tel:+19256557741"
            className="text-blue-600"
          >
            (925) 655-7741
          </a>
        </p>

        <p className="mb-2">
          Business Address:
          <br />
          Ashlar Building Group LLC
          <br />
          6200 Stoneridge Mall Rd, 3rd Floor
          <br />
          Pleasanton, CA 94588, United States
        </p>

        <p className="mb-2">
          Website:{" "}
          <a
            href="https://www.ashlarbuildinggroup.com"
            className="text-blue-600"
          >
            https://www.ashlarbuildinggroup.com
          </a>
        </p>

        <p className="mb-2">
          Privacy Policy:{" "}
          <Link
            to="/privacy-policy"
            className="text-blue-600"
          >
            https://www.ashlarbuildinggroup.com/privacy-policy
          </Link>
        </p>

        <p className="mb-2">
          California Contractor's License: #1130737
        </p>

      </div>
    </div>
  );
};

export default TermsAndConditions;